import { useEffect, useRef, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

export interface ProximaCita {
  id_cita: string;
  nombre_servicio: string;
  telefono: string | null;
  fecha_cita: string;
  hora_cita: string;
  hora_fin: string;
  estado_cita: string;
}

interface DashboardStats {
  citasHoy: number;
  citasPendientes: number;
  servicios: number;
  notificaciones: number;
  proximasCitas: ProximaCita[];
  noLaborableHoy: boolean;
}

const STATS_INICIALES: DashboardStats = {
  citasHoy: 0,
  citasPendientes: 0,
  servicios: 0,
  notificaciones: 0,
  proximasCitas: [],
  noLaborableHoy: false,
};

// Crear una cita dispara cambios en `citas` y en `usuario_en_cita` casi a la
// vez; sin esto se lanzarían dos consultas por cada alta.
const AGRUPAR_EVENTOS_MS = 300;

// TEMPORAL: traza para localizar por qué no se refresca. Quitar cuando vaya.
const traza = (...args: unknown[]) => console.log('[dashboardWorker]', ...args);

export const useDashboardWorker = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>(STATS_INICIALES);

  const empresaId = localStorage.getItem('id_empresa') || '';
  const userId = localStorage.getItem('id_usuario') || '';

  const idAgrupador = useRef<ReturnType<typeof setTimeout>>();

  // `silencioso` evita el parpadeo del spinner: en los refrescos por realtime
  // los datos ya están en pantalla y solo se sustituyen.
  const fetchStats = useCallback(
    async (silencioso = false) => {
      traza('1. consultando vista', { empresaId, userId, silencioso });

      if (!empresaId || !userId) {
        traza('1b. SIN empresaId o userId en localStorage');
        setLoading(false);
        return;
      }

      if (!silencioso) setLoading(true);

      const { data, error } = await supabase
        .from('vista_dashboard_trabajador')
        .select('*')
        .eq('id_empresa', empresaId)
        .eq('id_usuario', userId)
        .maybeSingle();

      if (error) {
        traza('2. ERROR en la vista:', error.message);
        Sentry.captureException(error, { extra: { empresaId, userId } });
        setLoading(false);
        return;
      }

      traza('2. fila recibida:', data ? {
        citas_hoy: data.citas_hoy,
        citas_pendientes: data.citas_pendientes,
        proximas: data.proximas_citas?.length ?? 0,
      } : 'NINGUNA (maybeSingle devolvió null)');

      if (data) {
        setStats({
          citasHoy: data.citas_hoy || 0,
          citasPendientes: data.citas_pendientes || 0,
          servicios: data.servicios || 0,
          notificaciones: data.notificaciones || 0,
          proximasCitas: data.proximas_citas || [],
          noLaborableHoy: data.no_laborable || false,
        });
      }

      setLoading(false);
    },
    [empresaId, userId]
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // =====================
  // REALTIME
  // =====================
  useEffect(() => {
    if (!empresaId || !userId) return;

    let canal: ReturnType<typeof supabase.channel> | null = null;
    let cancelado = false;

    const refrescar = (payload: { table?: string; eventType?: string }) => {
      traza('4. evento realtime:', payload?.table, payload?.eventType);
      clearTimeout(idAgrupador.current);
      idAgrupador.current = setTimeout(() => fetchStats(true), AGRUPAR_EVENTOS_MS);
    };

    // Se escuchan las dos tablas: la asignación al trabajador vive en
    // usuario_en_cita, y una cita puede pasar a ser suya (o dejar de serlo)
    // sin que la fila de `citas` cambie.
    const suscribir = (uid: string) => {
      if (cancelado || canal) return;

      canal = supabase
        .channel(`dashboard-trabajador-${uid}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'citas' },
          refrescar
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'usuario_en_cita' },
          refrescar
        )
        .subscribe((estado) => traza('3. estado del canal:', estado));
    };

    // getSession lee la sesión ya guardada, sin ida y vuelta al servidor:
    // con getUser, si la llamada de red fallaba o tardaba, el efecto se
    // salía en silencio y no llegaba a crearse el canal.
    supabase.auth.getSession().then(({ data: { session } }) => {
      traza('3a. sesión al montar:', !!session);
      if (session?.user) suscribir(session.user.id);
    });

    // Y si aún no estaba lista, se engancha en cuanto llegue.
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, session) => {
      if (session?.user) suscribir(session.user.id);
    });

    return () => {
      cancelado = true;
      clearTimeout(idAgrupador.current);
      sub.subscription.unsubscribe();
      if (canal) supabase.removeChannel(canal);
    };
  }, [empresaId, userId, fetchStats]);

  // Red de seguridad para cuando el socket se cae (móvil en segundo plano,
  // pérdida de cobertura): al volver a la pantalla se pide de nuevo.
  useEffect(() => {
    const alVolver = () => {
      if (document.visibilityState === 'visible') fetchStats(true);
    };

    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);

    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [fetchStats]);

  return { loading, stats, refetch: fetchStats };
};